import { createSelector } from '@reduxjs/toolkit';
import { QUESTION_DISPLAY_MODE } from '@hubertai/screening-question-types';
import type { TCreateQuestion } from 'components/Questionsv2/builder/context/create-question.context';
import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import type { RootState } from 'app/store';

type TOptionWithEnabled = {
  id: string;
  enabled?: Record<string, boolean>;
  replies?: Record<string, string[]>;
};

type TTemplateOptions = Record<
  string,
  {
    enabled?: Record<string, boolean>;
    replies?: Record<string, string[]>;
  }
>;

const mergeTemplateOptionVisibility = <T extends TOptionWithEnabled>(
  options: T[],
  templateOptions: TTemplateOptions | undefined,
  language: string | undefined,
): T[] => {
  if (!language) return options;

  let changed = false;
  const merged = options.map((option) => {
    const templateEnabledObj = templateOptions?.[option.id]?.enabled;
    const templateReplies = templateOptions?.[option.id]?.replies;

    const hasEnabledOverride =
      templateEnabledObj != null && Object.prototype.hasOwnProperty.call(templateEnabledObj, language);
    const hasRepliesOverride =
      templateReplies != null && Object.prototype.hasOwnProperty.call(templateReplies, language);

    if (!hasEnabledOverride && !hasRepliesOverride) {
      return option;
    }

    let next: T = option;

    if (hasEnabledOverride) {
      const nextEnabled = templateEnabledObj![language];
      if (option.enabled?.[language] !== nextEnabled) {
        changed = true;
        next = {
          ...next,
          enabled: {
            ...next.enabled,
            [language]: nextEnabled,
          },
        };
      }
    }

    if (hasRepliesOverride) {
      const nextReplies = templateReplies![language];
      if (option.replies?.[language] !== nextReplies) {
        changed = true;
        next = {
          ...next,
          replies: {
            ...next.replies,
            [language]: nextReplies,
          },
        };
      }
    }

    return next;
  });

  return changed ? merged : options;
};

export const makeSelectFullQuestion = () =>
  createSelector(
    [
      (state: RootState) => state.questionEditor.byId,
      (state: RootState) => state.Template?.present?.c?.parameters,
      (_: RootState, questionId: string) => questionId,
    ],
    (byId, templateParameters, questionId): TCreateQuestion => {
      const q = byId[questionId];
      const templateOptions = templateParameters?.[questionId]?.options;
      const language = q?.language;
      const shouldMergeTemplateOptions =
        q?.mode === ECreateQuestionMode.InTemplateEdit && q?.inTemplateEditAndUneditable;
      const mergedOptions = shouldMergeTemplateOptions
        ? mergeTemplateOptionVisibility(q.options ?? [], templateOptions, language)
        : (q.options ?? []);

      return {
        id: q.id,
        _id: q._id,
        version: q.version,
        type: q.type,
        short_description: q.short_description,
        language: q.language,
        availableLanguages: q.availableLanguages,
        source_question_id: q.source_question_id,
        presentation: q.presentation,
        conversation_settings: q.conversation_settings,
        visibility: q.visibility,
        client_id: q.client_id,
        access_groups_ids: q.access_groups_ids,
        intro: q.intro,
        text: q.text,
        outro: q.outro,
        clarify: q.clarify,
        plain_text: q.plain_text,
        options: mergedOptions,
        intents: q.intents,
        pre_actions: q.pre_actions,
        post_actions: q.post_actions,
        scoring_category: q.scoring_category,
        weight: q.weight,
        min_selections: q.min_selections,
        max_selections: q.max_selections,
        help_text: q.help_text,
        title: q.title,
        min_required: q.min_required,
        ranking_mode: q.ranking_mode,
        display_mode: q.display_mode,
        randomize_order: q.randomize_order,
        forms: q.forms,
        copiedIdMap: q.copiedIdMap ?? {},
        send_data_to_looker: q.send_data_to_looker,
        // Audio / video settings
        min_duration_seconds: q.min_duration_seconds,
        max_duration_seconds: q.max_duration_seconds,
        allow_retake: q.allow_retake,
        max_retakes: q.max_retakes,
        max_thinking_seconds: q.max_thinking_seconds,
        recording_quality: q.recording_quality,
        required: q.required,
        slot_filling: q.slot_filling,
        scheduling: q.scheduling,
        replies: q.replies,
        rules: q.rules,
      };
    },
  );

export const selectQuestion = (state: RootState, questionId: string) => state.questionEditor.byId[questionId];

export const selectQuestionMode = (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.mode;

export const selectShowDisabledOptions = (state: RootState) => state.Layout.showDisabledOptions ?? false;

export const selectShowOptionReferences = (state: RootState) => state.Layout.showOptionReferences ?? false;

export const selectQuestionHasOptionReferences = (state: RootState, questionId: string) =>
  selectQuestionOptions(state, questionId).some((option) => 'reference' in option && option.reference);

export const selectOptionReferencesVisible = (state: RootState, questionId: string) =>
  selectShowOptionReferences(state) || selectQuestionHasOptionReferences(state, questionId);

export const selectWritesTemplateOverrides = (state: RootState, questionId: string) => {
  const editor = state.questionEditor.byId[questionId];
  return editor?.mode === ECreateQuestionMode.InTemplateEdit && (editor.inTemplateEditAndUneditable ?? true);
};

export const selectQuestionInTemplateEditAndUneditable = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.inTemplateEditAndUneditable;

export const selectQuestionIsReadonly = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.isReadOnly;

export const selectQuestionIsPublishedOrNonExclusive = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.isPublishedOrNonExclusiveQuestion;

export const selectQuestionId = (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.id;

export const selectQuestionLanguage = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.language;

export const selectQuestionType = (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.type;

export const selectQuestionDescription = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.short_description;

export const selectQuestionPresentation = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.presentation;

export const selectQuestionOptions = createSelector(
  [
    (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.options,
    (state: RootState, questionId: string) => state.Template?.present?.c?.parameters?.[questionId]?.options,
    (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.language,
    (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.mode,
    (state: RootState, questionId: string) => state.questionEditor.byId[questionId]?.inTemplateEditAndUneditable,
  ],
  (options, templateParams, language, mode, inTemplateEditAndUneditable) => {
    if (!options) return [];
    if (mode !== ECreateQuestionMode.InTemplateEdit || !inTemplateEditAndUneditable) return options;
    return mergeTemplateOptionVisibility(options, templateParams, language);
  },
);

// Memoized id->index map, so each row does an O(1) lookup instead of its own .findIndex() scan.
const selectQuestionOptionIndexMap = createSelector([selectQuestionOptions], (options) => {
  const map = new Map<string, number>();
  options.forEach((option, index) => map.set(option.id, index));
  return map;
});

export const selectQuestionOptionIndexById = (state: RootState, questionId: string, optionId: string) =>
  selectQuestionOptionIndexMap(state, questionId).get(optionId) ?? -1;

export const selectQuestionMinSelection = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.min_selections;

export const selectQuestionMaxSelection = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.max_selections;

export const selectTemplateMinSelection = (state: RootState, questionId: string) =>
  state.Template?.present?.c?.parameters?.[questionId]?.min_selections;

export const selectTemplateMaxSelection = (state: RootState, questionId: string) =>
  state.Template?.present?.c?.parameters?.[questionId]?.max_selections;

export const selectQuestionHelpText = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.help_text;

export const selectAllQuestionIds = (state: RootState) => state.questionEditor.allIds;

export const selectAllQuestions = (state: RootState) => state.questionEditor.byId;

export const selectQuestionExists = (state: RootState, questionId: string) => questionId in state.questionEditor.byId;

export const selectQuestionRandomizeOrder = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.randomize_order ?? false;

export const selectQuestionSendDataToLooker = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.send_data_to_looker ?? false;

export const selectQuestionClassificationNames = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.classification_names;

// Cast until the published @hubertai/screening-question-types exposes `voice_settings`.
export const selectQuestionForceOptionsReadout = (state: RootState, questionId: string) =>
  (state.questionEditor.byId[questionId] as unknown as { voice_settings?: { force_options_readout?: boolean } })
    ?.voice_settings?.force_options_readout ?? false;

export const selectQuestionMinRequired = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.min_required;

export const selectTemplateMinRequired = (state: RootState, questionId: string) =>
  state.Template?.present?.c?.parameters?.[questionId]?.min_required;

export const selectQuestionRankingMode = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.ranking_mode ?? 'click';

export const selectQuestionDisplayMode = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.display_mode ?? QUESTION_DISPLAY_MODE.Buttons;

// Audio / video (recording) question settings
export const selectQuestionMaxDuration = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.max_duration_seconds;

export const selectQuestionMinDuration = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.min_duration_seconds;

export const selectQuestionAllowRetake = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.allow_retake ?? true;

export const selectQuestionMaxRetakes = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.max_retakes;

export const selectQuestionMaxThinkingSeconds = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.max_thinking_seconds;

export const selectQuestionRecordingRequired = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.required ?? false;

export const selectQuestionRecordingQuality = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.recording_quality ?? '480p';

// Scheduling (cal.com) question settings
export const selectQuestionReplies = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.replies;

export const selectQuestionScheduling = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.scheduling;

export const selectQuestionTitle = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.title ?? {};

export const selectQuestionPreviewRefreshKey = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.previewRefreshKey;

export const selectQuestionRules = (state: RootState, questionId: string) =>
  state.questionEditor.byId[questionId]?.rules ?? [];
